import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { TabsContext } from './tabs.context.js';
import { useDidImmutableValueChange } from '../../hooks/use-did-immutable-value-change.js';
import { useMap } from '../../hooks/use-map.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { useId } from '../../hooks/use-id.js';
import { RenderComp } from '../core/render-comp.component.js';

const propTypes = {
    /**
   * @deprecated omit to use default styling instead
   *
   * Whether or not the tabs have a shared fixed with
   */ fixedWidthHeaders: deprecatedProp(PropTypes.bool, 'Omit to use default styling instead'),
    /**
   * @deprecated omit to use default styling instead
   *
   * Whether the header spans the full width of its container
   */ fullWidthHeader: deprecatedProp(PropTypes.bool, 'Omit to use default styling instead'),
    /**
   * Whether or not the headers are centered horizontally in their container
   */ centerHeaders: PropTypes.bool,
    /**
   * Whether or not the headers are sticky.
   * Also hides the dividing line between the tab headers and content
   */ stickyHeaders: PropTypes.bool,
    /**
   * Whether or not the headers will wrap
   */ wrapHeaders: PropTypes.bool,
    /**
   * Whether or not there is a dividing line between headers and contents
   */ showDividingLine: PropTypes.bool,
    /**
   * @deprecated omit to use the standard size
   */ sizeVariant: deprecatedProp(PropTypes.oneOf([
        'standard',
        'mega'
    ]), DEPRECATED_PROP_MESSAGES.size),
    /**
   * @deprecated omit to use the standard size
   *
   * The size of the Tabs
   * Available options: 'standard', 'mega'
   */ size: deprecatedProp(PropTypes.oneOf([
        'standard',
        'mega'
    ]), 'Omit to use the size instead'),
    /**
   * The id of the tab which should be selected initially, used for uncontrolled tabs
   */ defaultSelectedTabId: PropTypes.string,
    /**
   * The id of the tab which should be selected. Used when controlling the state of the tabs
   */ selectedTabId: PropTypes.string,
    /**
   * Callback which is fired when the user requests a tab-change
   */ onRequestTabChange: PropTypes.func
};
const propKeysToRemove = Object.keys(propTypes);
const Tabs = renderWithRef('Tabs', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Tabs', SWAN_STYLE_KEY_MAP.tabs);
    const { children, fixedWidthHeaders = false, fullWidthHeader = false, centerHeaders = false, stickyHeaders = false, wrapHeaders = false, showDividingLine = true, sizeVariant = 'standard', size = sizeVariant, defaultSelectedTabId, selectedTabId: controlledSelectedTabId, onRequestTabChange: controlledOnRequestTabChange } = props;
    const classNames = new Set([
        'swan-vanilla-ignore',
        'swan-tabs'
    ]);
    if (fullWidthHeader) classNames.add('swan-tabs-full');
    if (fixedWidthHeaders) classNames.add('swan-tabs-fixed-width-headers');
    if (centerHeaders) classNames.add('swan-tabs-center-headers');
    if (stickyHeaders) classNames.add('swan-tabs-sticky-headers');
    if (wrapHeaders) classNames.add('swan-tabs-wrap-headers');
    if (showDividingLine === false) classNames.add('swan-tabs-no-dividing-line');
    if (size === 'mega') classNames.add('swan-tabs-mega');
    const isControlled = controlledSelectedTabId !== undefined;
    const didIsControlledChange = useDidImmutableValueChange(isControlled);
    warning(!didIsControlledChange, isControlled ? 'The `Tabs` component switched from "Uncontrolled" to "Controlled". This is an unsupported pattern. This can happen if `selectedTabId` was not initially defined, but was defined in a subsequent render' : 'The `Tabs` component switched from "Controlled" to "Uncontrolled". This is an unsupported pattern. This can happen if `selectedTabId` was initially defined, but not defined in a subsequent render. If you\'re trying to make it so that _no_ tab is selected, you should pass `null` for `selectedTabId`');
    warning(isControlled || defaultSelectedTabId !== undefined, '`defaultSelectedTabId` is required if `selectedTabId` is not provided. If you really want there to be _no_ tab selected initially, pass `null` for `defaultSelectedTabId`');
    const [headerIds, { set: registerHeader, remove: unregisterHeader }] = useMap();
    const [labelIds, { set: registerLabel, remove: unregisterLabel }] = useMap();
    const [contentIds, { set: registerContent, remove: unregisterContent }] = useMap();
    const [selectedTabId, requestTabChange] = useControllableValue({
        value: controlledSelectedTabId,
        onChange: controlledOnRequestTabChange || undefined,
        // if defaultSelectedTabId is not defined, we'll use `null` (no value selected by default)
        // when the Tabs are controlled, this value is just ignored, and when the Tabs are uncontrolled, we warn the user that this is happening implicitly.
        // the only reason we're doing this defaulting here rather than in the props destructuring at the top (where defaulting usually happens)
        // is so that we can catch the case where we're implicitly defaulting to null and we can warn the user
        defaultValue: defaultSelectedTabId !== null && defaultSelectedTabId !== void 0 ? defaultSelectedTabId : null,
        defaultOnChange: (setUncontrolledState, ...onChangeArgs)=>{
            const [newTabId] = onChangeArgs;
            setUncontrolledState(newTabId);
        }
    });
    const tabsName = useId();
    return /*#__PURE__*/ React.createElement(TabsContext.Provider, {
        value: {
            tabsName,
            selectedTabId,
            onRequestTabChange: requestTabChange,
            headerIds,
            registerHeader,
            unregisterHeader,
            labelIds,
            registerLabel,
            unregisterLabel,
            contentIds,
            registerContent,
            unregisterContent
        }
    }, /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        forwardedRef: ref,
        propKeysToRemove: propKeysToRemove,
        classNames: classNames,
        props: props
    }, children));
});
Tabs.defaultProps = {
    showDividingLine: true
};

export { Tabs };
